import { computed, ref, watch, nextTick } from 'vue';
import type { Ref } from 'vue';

/**
 * Composable for managing stacked workflow view
 * Handles topological sorting, depth calculation, and expand/collapse state
 */
export function useStackedWorkflow(
  nodes: Ref<any[]>,
  edges: Ref<any[]>
) {
  // Expand/collapse state
  const expandedNodes = ref(new Set<string>());
  const stackContainer = ref<HTMLElement | null>(null);

  /**
   * Build a map of parent nodes for each node
   */
  const parentsMap = computed(() => {
    const map = new Map<string, string[]>();

    // Initialize all nodes with empty parent arrays
    nodes.value.forEach(node => {
      map.set(node.id, []);
    });

    // Populate parent relationships from edges
    edges.value.forEach(edge => {
      const parents = map.get(edge.target) || [];
      if (!parents.includes(edge.source)) {
        parents.push(edge.source);
      }
      map.set(edge.target, parents);
    });

    return map;
  });

  /**
   * Build a map of child nodes for each node
   */
  const childrenMap = computed(() => {
    const map = new Map<string, string[]>();

    // Initialize all nodes with empty children arrays
    nodes.value.forEach(node => {
      map.set(node.id, []);
    });

    // Populate child relationships from edges
    edges.value.forEach(edge => {
      const children = map.get(edge.source) || [];
      if (!children.includes(edge.target)) {
        children.push(edge.target);
      }
      map.set(edge.source, children);
    });

    return map;
  });

  /**
   * Get root nodes (nodes with no parents)
   */
  const rootNodes = computed(() => {
    return nodes.value.filter(node => {
      const parents = parentsMap.value.get(node.id) || [];
      return parents.length === 0;
    });
  });

  /**
   * Perform depth-first topological sort
   * Returns nodes with children immediately following parents
   * This minimizes vertical line overlaps in the stacked view
   */
  const sortedNodes = computed(() => {
    const sorted: any[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();

    // Depth-first visit function
    function dfsVisit(nodeId: string) {
      if (visited.has(nodeId)) return;
      if (visiting.has(nodeId)) {
        console.warn('Cycle detected in workflow graph');
        return;
      }

      visiting.add(nodeId);

      // Visit all parents first (ensures topological order)
      const parents = parentsMap.value.get(nodeId) || [];
      parents.forEach(parentId => {
        dfsVisit(parentId);
      });

      visiting.delete(nodeId);
      visited.add(nodeId);

      // Add this node to sorted list
      const node = nodes.value.find(n => n.id === nodeId);
      if (node) {
        sorted.push(node);
      }

      // Then visit children (depth-first)
      const children = childrenMap.value.get(nodeId) || [];
      children.forEach(childId => {
        dfsVisit(childId);
      });
    }

    // Start with root nodes
    rootNodes.value.forEach(rootNode => {
      dfsVisit(rootNode.id);
    });

    // Visit any remaining nodes (in case of disconnected graph)
    nodes.value.forEach(node => {
      if (!visited.has(node.id)) {
        dfsVisit(node.id);
      }
    });

    return sorted;
  });

  /**
   * Calculate depth for each node (distance from root)
   * Uses BFS to ensure consistent depths for nodes with multiple parents
   */
  const nodeDepthMap = computed(() => {
    const depthMap = new Map<string, number>();
    const visited = new Set<string>();
    const queue: Array<{ id: string; depth: number }> = [];

    // Start with root nodes at depth 0
    rootNodes.value.forEach(node => {
      queue.push({ id: node.id, depth: 0 });
      visited.add(node.id);
    });

    // BFS traversal
    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;

      // Set depth (or keep maximum if already set from another parent)
      const currentDepth = depthMap.get(id) ?? -1;
      depthMap.set(id, Math.max(currentDepth, depth));

      // Add children to queue
      const children = childrenMap.value.get(id) || [];
      children.forEach(childId => {
        if (!visited.has(childId)) {
          visited.add(childId);
          queue.push({ id: childId, depth: depth + 1 });
        } else {
          // Update depth if we found a longer path
          const childDepth = depthMap.get(childId) ?? 0;
          if (depth + 1 > childDepth) {
            queue.push({ id: childId, depth: depth + 1 });
          }
        }
      });
    }

    return depthMap;
  });

  /**
   * Get depth for a specific node
   */
  function getNodeDepth(nodeId: string): number {
    return nodeDepthMap.value.get(nodeId) ?? 0;
  }

  /**
   * Get children for a specific node
   */
  function getChildren(nodeId: string): string[] {
    return childrenMap.value.get(nodeId) || [];
  }

  /**
   * Get parents for a specific node
   */
  function getParents(nodeId: string): string[] {
    return parentsMap.value.get(nodeId) || [];
  }

  /**
   * Check if node has multiple parents
   */
  function hasMultipleParents(nodeId: string): boolean {
    const parents = parentsMap.value.get(nodeId) || [];
    return parents.length > 1;
  }

  /**
   * Get parent count for a node
   */
  function getParentCount(nodeId: string): number {
    const parents = parentsMap.value.get(nodeId) || [];
    return parents.length;
  }

  /**
   * Toggle expand/collapse for a node
   */
  function toggleExpand(nodeId: string) {
    if (expandedNodes.value.has(nodeId)) {
      expandedNodes.value.delete(nodeId);
    } else {
      expandedNodes.value.add(nodeId);
    }

    // Trigger reactivity
    expandedNodes.value = new Set(expandedNodes.value);
  }

  /**
   * Expand all nodes
   */
  function expandAll() {
    const allIds = nodes.value.map(n => n.id);
    expandedNodes.value = new Set(allIds);
  }

  /**
   * Collapse all nodes
   */
  function collapseAll() {
    expandedNodes.value = new Set();
  }

  return {
    // Reactive state
    expandedNodes,
    stackContainer,

    // Computed properties
    sortedNodes,
    nodeDepthMap,
    childrenMap,
    parentsMap,
    rootNodes,

    // Functions
    getNodeDepth,
    getChildren,
    getParents,
    hasMultipleParents,
    getParentCount,
    toggleExpand,
    expandAll,
    collapseAll,
  };
}
